import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, X } from 'lucide-react';
import API from '../lib/api';
import findMessage from '../utils/messages';
import { Roles, IDType } from '../utils/constants';
import video from '../assets/videos/5.mp4';
import videoThumbnail from '../assets/videos/5.png'; // Add your thumbnail image

import RegisterHeader from '../components/common/register/RegisterHeader';
import Inputbox from '../components/common/Inputbox';
import NormalButton from '../components/common/NormalButton';
import NormalSelectbox from '../components/common/NormalSelectbox';
import PhoneNumberInputbox from '../components/common/PhoneNumberInputbox';
import IdentificationInputbox from '../components/common/IdentificationInputbox';
import Notification from '../components/common/Notification';

export default function Register() {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [role, setRole] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [errorEmail, setErrorEmail] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPass, setConfirmPass] = useState('');
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [isAcceptedPolicy, setIsAcceptedPolicy] = useState(false);
  const [phonenumber, setPhonenumber] = useState({
    nationality: {
      country: '',
      areaCode: '',
      flag: '',
    },
    number: '',
  });
  const [idType, setIdType] = useState('');
  const [citizenData, setCitizenData] = useState({
    nationality: {
      flag: '',
      areaCode: '',
      country: '',
      isoCode3: '',
    },
    number: '',
  });
  const [notification, setNotification] = useState(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Show tooltip after a short delay when component mounts
    const timer = setTimeout(() => {
      setShowTooltip(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (videoRef.current && showVideoDialog) {
      videoRef.current.load(); // Force reload the video when dialog opens
    }
  }, [showVideoDialog]);

  const onChangePhonenumber = (type, value) => {
    let newPhone = { ...phonenumber };
    newPhone[type] = value;
    setPhonenumber(newPhone);
  };
  const onChangeCitizenData = (type, value) => {
    let newCitizenData = { ...citizenData };
    newCitizenData[type] = value;
    setCitizenData(newCitizenData);
  };

  const onRegister = async () => {
    await API.post('auth/register', {
      firstName: firstName,
      lastName: lastName,
      email: email,
      password: password,
      idType: idType,
      country: citizenData.nationality.country,
      citizenId: citizenData.number,
      phonenumber: {
        nationality: {
          country: phonenumber.nationality.country,
          area_code: phonenumber.nationality.areaCode,
        },
        number: phonenumber.number,
      },
      role: role,
    })
      .then(() => {
        navigate(`/register/confirm-email/${email}`);
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message;
        console.log('err: ', msg);

        if (msg == 'User already exists') setErrorEmail(true);
        else {
          setNotification({
            message: findMessage(msg),
            type: 'error',
          });
        }
      });
  };

  const isStrongPassword = () => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return regex.test(password) && password == confirmPass;
  };
  const fullInput = () => {
    return !role ||
      !firstName ||
      !lastName ||
      !email ||
      !password ||
      password != confirmPass ||
      !isStrongPassword() ||
      !phonenumber.nationality.areaCode ||
      !phonenumber.number ||
      !idType ||
      !citizenData.nationality.country ||
      !citizenData.number ||
      !isAcceptedPolicy
      ? false
      : true;
  };

  const handleSupportClick = () => {
    setShowTooltip(false);
    setShowVideoDialog(true);
  };

  const handleCloseDialog = () => {
    setShowVideoDialog(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#E7EFFF]">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Video Dialog */}
      {showVideoDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black"
          onClick={handleCloseDialog}
        >
          <div
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseDialog}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors"
            >
              <X size={24} className="text-gray-600" />
            </button>

            <div className="w-full h-full flex items-center justify-center p-4">
              <video
                ref={videoRef}
                controls
                autoPlay
                loop
                poster={videoThumbnail}
                preload="metadata"
                className="max-w-full max-h-full object-contain"
                style={{ aspectRatio: 'auto' }}
              >
                <source src={video} type="video/mp4" />
                Tu navegador no soporta el elemento de video.
              </video>
            </div>
          </div>
        </div>
      )}

      <div className="w-full lg:w-[1044px] bg-white rounded-[20px] shadow-xl ">
        {/* Top header */}
        <RegisterHeader currentStep={0} onClose={() => navigate('/')} />

        {/* Body */}
        <div className="relative w-full py-8 md:py-12 px-8 md:px-20 lg:px-52 space-y-8 bg-[#F9F9F9] rounded-b-[0px] lg:rounded-b-[20px]">
          <div className="space-y-6">
            <div className="space-y-3.5">
              <h3 className="font-[poppins] text-sm md:text-lg lg:text-xl text-[#252B37] font-semibold leading-[20px]">
                Configurá tus credenciales de ingreso
              </h3>
              <p className="font-[poppins] text-xs sm:text-sm text-[#414651] font-normal leading-[20px]">
                Selecciona el tipo de cuenta que deseas crear, y completa con tus datos personales.
              </p>
            </div>

            {/* form group */}
            <NormalSelectbox
              title="Tipo de usuario"
              options={Roles}
              selected={role}
              onChange={setRole}
            />

            <div className="grid grid-cols-2 gap-4">
              <Inputbox
                title="Nombre"
                placeholder="Juan"
                value={firstName}
                onChange={setFirstName}
              />
              <Inputbox
                title="Apellido"
                placeholder="Perez"
                value={lastName}
                onChange={setLastName}
              />
            </div>

            <div className="space-y-1">
              <Inputbox
                title={'Correo electrónico'}
                placeholder={'ejemplo@mail.com'}
                value={email}
                onChange={setEmail}
              />
              <p className="font-[poppints] text-xs text-[#F04438] font-normal">
                {errorEmail && 'La usuario ya existe'}
              </p>
            </div>

            <div className="space-y-1">
              <div className="font-[poppins] text-xs text-[#717680] font-normal leading-[20px] tracking-[0%]">
                Contraseña
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña123#"
                  className="w-full font-[poppins] text-sm placeholder-[#A4A7AE] text-[#414651] font-normal focus:outline-none focus:border-[#414651] bg-white border border-[#C2C2C2] py-2 pl-3 pr-10 rounded-[10px]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2.5 top-2.5 text-gray-500"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <p className="font-[poppins] text-xs font-normal leading-[20px]">
                <span
                  className={password.length < 8 ? 'text-[#A4A7AE]' : 'text-[#027A48]'}
                >{`Incluir ${password.length < 8 ? '•' : '✓'}8 caractéres`}</span>
                <span
                  className={/[A-Z]/.test(password) ? 'text-[#027A48]' : 'text-[#A4A7AE]'}
                >{` o más ${/[A-Z]/.test(password) ? '✓' : '•'} mayúscula`}</span>
                <span className={/\d/.test(password) ? 'text-[#027A48]' : 'text-[#A4A7AE]'}>{` ${
                  /\d/.test(password) ? '✓' : '•'
                } 1 número`}</span>
                <span
                  className={/[^a-zA-Z0-9]/.test(password) ? 'text-[#027A48]' : 'text-[#A4A7AE]'}
                >{` ${
                  /[^a-zA-Z0-9]/.test(password) ? '✓' : '•'
                } 1 símbolo especial ( !, # ó $).`}</span>
              </p>
            </div>

            <div className="space-y-1">
              <div className="font-[poppins] text-xs text-[#424242] font-normal leading-[20px] tracking-[0%]">
                Repetir Contraseña
              </div>
              <div className="relative">
                <input
                  type={showConfirmPass ? 'text' : 'password'}
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  placeholder="Contraseña123#"
                  className="w-full font-[poppins] text-sm placeholder-[#A4A7AE] text-[#414651] font-normal focus:outline-none focus:border-[#414651] bg-white border border-[#C2C2C2] py-2 pl-3 pr-10 rounded-[10px]"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPass((prev) => !prev)}
                  className="absolute right-2 top-3 text-gray-500 cursor-pointer"
                >
                  {showConfirmPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="font-[poppins] text-xs font-normal text-gray-400 leading-[20px] tracking-[0%]">
                <span
                  className={`${
                    password.length && password == confirmPass ? 'text-[#027A48]' : ''
                  }`}
                >
                  • Coincidencia de contraseñas
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6">
              <NormalSelectbox
                title="Tipo de identificación"
                options={IDType}
                selected={idType}
                onChange={setIdType}
              />
              <IdentificationInputbox
                citizenData={citizenData}
                onChange={(type, value) => onChangeCitizenData(type, value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6">
              <PhoneNumberInputbox
                phonenumber={phonenumber}
                onChange={(type, value) => onChangePhonenumber(type, value)}
              />
            </div>
          </div>

          <div className="h-auto flex items-start font-[poppins] text-xs font-semibold text-[#181D27] leading-[20px] tracking-[0%]">
            <input
              type="checkbox"
              value={isAcceptedPolicy}
              onChange={() => setIsAcceptedPolicy(!isAcceptedPolicy)}
              className="form-checkbox h-5 w-5 cursor-pointer mr-2"
            />
            <div className="flex-1">
              Acepto los
              <button
                onClick={() => navigate('/termsOfServices')}
                className="text-[#092F7B] px-1 cursor-pointer hover:underline"
              >
                Términos y Condiciones
              </button>
              y la{' '}
              <button
                onClick={() => navigate('/policy')}
                className="text-[#092F7B] px-1 cursor-pointer hover:underline"
              >
                {' '}
                Política de Privacidad
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <NormalButton
              disabled={fullInput() ? false : true}
              customClass={fullInput() ? 'bg-[#092F7B] text-white px-18' : 'px-18 text-[#A4A7AE]'}
              text={'Continuar'}
              onClick={onRegister}
            />
          </div>

          {/* Footer with Tooltip */}
          <div className="pt-2 font-[poppins] text-xs text-gray-600 font-semibold leading-[20px] tracking-[0%] relative">
            ¿Necesitas ayuda?{' '}
            <span className="relative inline-block">
              <button
                onClick={handleSupportClick}
                className="font-[500] text-[#092F7B] hover:underline cursor-pointer"
              >
                Ver guia paso a paso
              </button>

              {/* Tooltip */}
              {showTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-10 animate-bounce">
                  <div className="relative bg-[#092F7B] text-white text-xs rounded-lg py-2 px-4 shadow-lg whitespace-nowrap">
                    Por favor, regístrate en este sitio después de revisar este video
                    <button
                      onClick={() => setShowTooltip(false)}
                      className="ml-2 text-white hover:text-gray-200"
                    >
                      ✕
                    </button>
                    {/* Arrow pointing down */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#092F7B]"></div>
                  </div>
                </div>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
